import ApparelBom from "../Model/ApparelBom";

define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"csi",
	"csi/_base/underscore",
	"csi/comm/PostRequest",
	"csi/config",
	"csi/def/Action",
	"csi/locale",
	"csi/store/Names",
	"csi/store/_base",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryFilterBuilder"
], function(declare, Memory, csi, _, PostRequest, config, Action, locale, Names, csiStore, xml, Query, QueryFilterBuilder) {

	const LOCK_HOST_PLACEMENTS_FOR_SOURCES = "LockHostPlacementsForSources";

	/**
	 * Return section for given group action context
	 * @param {Context} ctx
	 * @return {Node}
	 */
	function _getSectionFromActionContext(ctx) {
		return ctx.cache.get(ctx.queryResult.groupValue);
	}

	return declare(null, {
		/**
		 * Do not allow edit section on non ad hoc group or blanks section
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionEditSection: function(ctx, action) {
			const section = _getSectionFromActionContext(ctx);
			return section && section.AdHoc && xml.nodeHasAccess(section, xml.RIGHT_WRITE);
		},

		/**
		 * Allows sections to be deleted if view node has Admin Rights
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionDeleteSection: function(ctx, action) {
			return xml.nodeHasAccess(this.getViewNode(), xml.RIGHT_ADMIN);
		},

		/**
		 * Checks if it's a Product Source BOM
		 * @return {boolean}
		 */
		isSourceBOM(parentNode) {
			if (parentNode == null) {
				return false;
			}
			return this.cacheView.getNode(parentNode.__Parent__)?.$Type === "ProductSource";
		},

		/**
		 * Do not allow push section action on blanks group and without BOM Editor Roles
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionPushSection: function(ctx, action) {
			if (ApparelBom.userHasBomEditorRole(ctx.getNode())) {
				const {queryResult} = ctx;
				return !!queryResult && queryResult.getGroupValue() !== xml.VALUE_BLANK;
			}
			return false;
		},

		// Boolean: optional filters using BOM section definitions (assuming grouped by BOM sections)
		applyBomSectionQuery: function(groupValue, q, cache, onProductType) {
			// filter on ApplicablePlacementProductTypes of the BOMSectionDefinition if we are grouped
			if (!xml.isValidURL(groupValue))
				return; // can't do anything without a proper group value node

			const bomSectionDef = cache.getNode(groupValue);
			if (!bomSectionDef || bomSectionDef.$Type !== "BOMSectionDefinition")
				return; // can't do anything with a non-DSSection group value

			// if the bom section is based on a definition (not ad-hoc)
			const appPlacementPTs = bomSectionDef.PlacementProductTypes;

			if (appPlacementPTs && appPlacementPTs.length > 0) {
				const qor = q.Or();
				appPlacementPTs.forEach(function(apt) {
					// filter by URL if on product type
					if (onProductType) {
						qor.Node("URL", xml.OP_EQUALS, apt);
					} else {
						qor.RefAttr("ProductType", xml.OP_EQUALS, apt);
					}
				});
			}

			const bomQueryFilter = bomSectionDef.QueryFilter;
			if (!onProductType && xml.isValidURL(bomQueryFilter)) {
				QueryFilterBuilder.create(q, bomQueryFilter, groupValue, cache);
			}
		},

		/**
		 * Creates an ad-hoc field store for BOM sections
		 * @param {*} actionCtx - Action context for BOM section
		 * @param {*} material - Source node of action (Type: Material)
		 * @returns
		 */
		getBOMSectionStore: function(actionCtx, material) {
			let request = new PostRequest("Specification", "GetMatchingBOMSections").addParameter("Material", material)

			const {isApparelBOMAction, isFoodBOMAction, isThemeBOMAction} = actionCtx;
			if (isApparelBOMAction) {
				request.addParameter("ProductType", "Style");
			} else if (isFoodBOMAction) {
				request.addParameter("ProductType", "Food");
			} else if (isThemeBOMAction) {
				request.addParameter("ProductType", "Theme");
			} else {
				request.addParameter("ProductType", "Material");
			}

			return new Names({
				customOperation: true,
				typeNode: "BOMSectionDefinition",
				request: request,
				data: [csiStore.nameObject("")]
			});
		},

		/**
		 * Creates a Memory with sections passed.
		 * @param {Node[]} sections - All sections that we want to put in Memory.
		 * @param {ICache} cache - The cache that we query the nodes from and also where we get localized node name from.
		 * @return {Memory} - Memory that has localized name of the sections passed with their URLs.
		 */
		_getGroupDefaultsStore: function(sections, cache) {
			return new Memory({
				idProperty: "id",
				labelProperty: "id",
				urlProperty: "url",
				data: _.map(cache.getNodes(sections), function(node) {
					return {
						id: locale.getNodeName(node, cache),
						url: node.$URL
					};
				})
			});
		},

		/**
		 * Filter by season availability if on Style BOM or Theme BOM
		 * @param {Query} query
		 * @param {Context} [ctx]
		 */
		_applySeasonAvailabilityFilters(query, ctx) {
			const pm = ctx ? ctx.getNode() : this.getNode();
			const cache = ctx ? ctx.cache : this.getCache();
			const parent = ctx ? ctx.getParent() : this.getParent();
			const bomParent = cache.getParentOfType(parent, ["Style", "Theme"]);
			let seasonURLs = null;
			if (bomParent) {
				seasonURLs = bomParent.ParentSeason ? [bomParent.ParentSeason] : bomParent.ThemeSeasons;
				seasonURLs = seasonURLs.filter(url => xml.isValidURL(url))
			}
			if ((seasonURLs && (!pm || !pm.ForStyle)) && !(config.getSubtypeSetting(cache.getNode(bomParent.__Parent__)?.Subtype, "ThemeToSeason") === "ThemeToSeason:AllSeasons")) {
				const qOr = query.Or();
				qOr.Attr2("SeasonAvailability", xml.OP_EQUALS, {"Dimension": 0, "Path": "Child:Spec"});
				seasonURLs.forEach(seasonUrl => {
					qOr.RefAttr("AvailabilitySeason", xml.OP_EQUALS, seasonUrl, "Child:Spec/Child:SeasonAvailability");
				});
			}
		},

		/**
		 * Apply UsableForStyleBOM or UsableForMaterialBOM filters
		 * @param {Query} query
		 * @param {Context} [ctx]
		 */
		_applyUsableFilters(query, ctx) {
			const parent = ctx ? ctx.getParent() : this.getParent();
			const parentType = parent && parent.$Type;
			const usableForBOM =
				parentType && parentType === "MaterialBOMRevision"
					? "UsableForMaterialBOM"
					: "UsableForStyleBOM";
			query.Attr(usableForBOM, xml.OP_EQUALS, true);
		},

		/**
		 * Check if EnforceSeasonAvailability is set for the subtype
		 * @param {Context} [ctx]
		 * @return {boolean}
		 */
		_isSeasonAvailabilityEnforced(ctx) {
			const cache = ctx ? ctx.cache : this.getCache();
			const parent = ctx ? ctx.getParent() : this.getParent();
			const bom = cache.getNode(parent.__Parent__);
			return bom && xml.isValidURL(bom.Subtype) && config.getSubtypeSetting(bom.Subtype, "EnforceSeasonAvailability");
		}
	});
});
